/**
 * Follow-band items and venue-page show rows → the raw feed-row shape that
 * `normalizeCardData` (EVENT / EVENT_GROUP) understands, so every surface
 * renders events through the same unified card.
 *
 * Extracted and tested for the same reason feedEventShape.js was: each alias
 * here is a field name that differs between what a server endpoint emits and
 * what the card layer reads, and getting one wrong degrades the UI without
 * throwing anything (the photo alias in the old ShowCard mapping shipped
 * wrong once — every ranked card rendered the fallback glyph).
 *
 * Ground truth for the inputs:
 *  - band items: `server/services/followingService.js` `_venueItem` /
 *    `_artistItem` (kind, group_id/event_id/venue_id, event_date, image_url…)
 *  - venue show rows: `server/services/venueFollowsService.js`
 *    `getUpcomingShowsStmt` (row_kind, show_key, event_date, image_url…)
 * Check those before trusting a field name here.
 */

const joinLocation = (venueName, city) =>
  [venueName, city].filter(Boolean).join(', ') || null;

/**
 * Map one follow-band item (bands endpoint, `newly`/`upcoming`) onto the
 * feed-row shape. Grouped items become `event_group` rows (internal group
 * page), lone events become `event` rows (internal venue page via venue_id).
 *
 * @param {object}  item          band item from followingService
 * @param {object}  [opts]
 * @param {boolean} [opts.isNew]  render the "New" pill (CardBadges.NewBadge)
 */
export const bandItemToFeedItem = (item, { isNew = false } = {}) => {
  const isGroup = item.kind === 'group';
  // Provenance guard: on a card row, `source` must be the source-system
  // STRING (ticketmaster / uitdatabank / …). The bands endpoint names its
  // follow descriptor `follow_source` for exactly this reason — but if an
  // object ever arrives under `source` (an older payload shape), passing it
  // through would shadow source_type in the normalizers and silently kill
  // the publiq-required UiT credit.
  const { source, ...rest } = item;
  return {
    ...rest,
    ...(typeof source === 'string' ? { source } : {}),
    content_type: isGroup ? 'event_group' : 'event',
    id: isGroup ? item.group_id : item.event_id,
    // Feed rows carry the photo on cover_photo too; the schedule/save modal
    // payload builders read that name, the carousel reads image_url.
    cover_photo: item.image_url || null,
    location: joinLocation(item.venue_name, item.city),
    // buildSchedulePayload reads these raw-row names.
    venue: item.venue_name || null,
    category: item.genre || null,
    _isNew: isNew || undefined
  };
};

/**
 * Map one venue-page show row (getUpcomingShows) onto the feed-row shape.
 * The venue id is not part of the row (the query filters by it), so the
 * page passes its own.
 *
 * @param {object}  show
 * @param {string}  venueId
 * @param {object}  [opts]
 * @param {boolean} [opts.hideVenueFollow] suppress the card's follow chip —
 *   the venue page passes true because its hero owns the follow affordance,
 *   and a second, unsynchronized control per card can contradict it.
 */
export const venueShowToFeedItem = (show, venueId, { hideVenueFollow = false } = {}) => {
  const isGroup = show.row_kind === 'group';
  return {
    ...show,
    content_type: isGroup ? 'event_group' : 'event',
    id: show.show_key,
    group_id: isGroup ? show.show_key : undefined,
    venue_id: venueId,
    _hideVenueFollow: hideVenueFollow || undefined,
    // Rows that carry only the broad segment would otherwise blank the
    // category line (the toShow lesson, kept).
    genre: show.genre || show.primary_segment || null,
    cover_photo: show.image_url || null,
    location: joinLocation(show.venue_name, show.city),
    // buildSchedulePayload reads these raw-row names.
    venue: show.venue_name || null,
    category: show.genre || show.primary_segment || null
  };
};
